/**
 * The host's choice of weapons (author's addition), in the setup of a match on this PC and in « Créer la room »: a
 * window with every weapon, its starting ammunition for each team (none, 1 to 9, unlimited) and, for the chest
 * weapons, whether a chest may hold them. The last choice is kept in the browser.
 */
import { WEAPONS, MAX_START_AMMO, defaultArsenal, encodeArsenal, decodeArsenal } from '@invaders/game';
import { weaponIcon } from './weapon-icons';
import { sound } from './sound';

export interface Arsenal {
  ammo: number[];
  chestWeapons: number[];
}

const KEY = 'invaders.arsenal';

function load(): Arsenal {
  try {
    const d = decodeArsenal(localStorage.getItem(KEY) ?? '');
    if (d.ammo && d.chestWeapons) return { ammo: d.ammo, chestWeapons: d.chestWeapons };
  } catch { /* storage unavailable: the GDD's */ }
  return defaultArsenal();
}

function save(a: Arsenal): void {
  try { localStorage.setItem(KEY, encodeArsenal(a.ammo, a.chestWeapons)); } catch { /* not kept */ }
}

/** True if `a` is the GDD's arsenal. */
export function isDefaultArsenal(a: Arsenal): boolean {
  const d = defaultArsenal();
  return encodeArsenal(a.ammo, a.chestWeapons) === encodeArsenal(d.ammo, d.chestWeapons);
}

/** One line about an arsenal, for the setup screens and the room. */
export function arsenalSummary(a: Arsenal): string {
  if (isDefaultArsenal(a)) return 'Standard arsenal';
  const start = a.ammo.filter((n) => n !== 0).length;
  return `Chosen arsenal: ${start} weapon${start > 1 ? 's' : ''} at the start, ${a.chestWeapons.length} in the chests`;
}

const label = (n: number) => (n < 0 ? '∞' : String(n));
/** − and + walk through 0, 1 … 9, ∞; a chest weapon stops at 9 (never unlimited: the author's rule). */
const step = (n: number, dir: 1 | -1, chest: boolean) => {
  if (dir < 0) return n < 0 ? MAX_START_AMMO : Math.max(0, n - 1);
  if (n < 0) return -1;
  return n >= MAX_START_AMMO ? (chest ? MAX_START_AMMO : -1) : n + 1;
};

/**
 * Wires `button` to open the window, and `summary` to show the choice. Returns the current choice (a copy).
 */
export function setupArsenalPicker(button: HTMLElement, summary: HTMLElement, onChange?: () => void): () => Arsenal {
  let current = load();
  const show = () => { summary.textContent = arsenalSummary(current); };
  show();

  button.onclick = () => {
    sound.click();
    let draft: Arsenal = { ammo: [...current.ammo], chestWeapons: [...current.chestWeapons] };
    const overlay = document.createElement('div');
    overlay.className = 'overlay arsenal-overlay';
    const card = (i: number) => `
      <div class="wcard${WEAPONS[i].chest ? ' chest' : ''}" data-w="${i}">
        <img src="${weaponIcon(i)}" alt="" />
        <b>${WEAPONS[i].name}</b>
        <span class="stepper"><button data-d="-1" title="Less">−</button><i></i><button data-d="1" title="More">+</button></span>
        ${WEAPONS[i].chest ? '<label class="inchest"><input type="checkbox" /> chests</label>' : ''}
      </div>`;
    const base = WEAPONS.map((_, i) => i).filter((i) => !WEAPONS[i].chest);
    const chest = WEAPONS.map((_, i) => i).filter((i) => WEAPONS[i].chest);
    overlay.innerHTML = `
      <div class="card arsenal">
        <h1>Weapons of the match</h1>
        <p class="sub">Each team's ammunition at the start (∞: unlimited, not for the chest weapons). The ticked chest weapons may fall from the sky; the medkit always may.</p>
        <h2>Base arsenal</h2>
        <div class="arsenal-grid">${base.map(card).join('')}</div>
        <h2>Chest weapons</h2>
        <div class="arsenal-grid">${chest.map(card).join('')}</div>
        <div class="buttons"><button data-a="gdd">Standard arsenal</button><button data-a="cancel">Cancel</button></div>
        <button class="primary" data-a="ok">Confirm</button>
      </div>`;
    document.body.appendChild(overlay);
    const refresh = () => {
      overlay.querySelectorAll<HTMLElement>('.wcard').forEach((el) => {
        const i = Number(el.dataset.w);
        el.querySelector('i')!.textContent = label(draft.ammo[i]);
        el.classList.toggle('off', draft.ammo[i] === 0 && !draft.chestWeapons.includes(i));
        const box = el.querySelector<HTMLInputElement>('input');
        if (box) box.checked = draft.chestWeapons.includes(i);
      });
    };
    overlay.querySelectorAll<HTMLElement>('.wcard').forEach((el) => {
      const i = Number(el.dataset.w);
      el.querySelectorAll<HTMLButtonElement>('.stepper button').forEach((b) => {
        b.onclick = () => { draft.ammo[i] = step(draft.ammo[i], Number(b.dataset.d) as 1 | -1, WEAPONS[i].chest); sound.click(); refresh(); };
      });
      const box = el.querySelector<HTMLInputElement>('input');
      if (box) box.onchange = () => {
        draft.chestWeapons = box.checked ? [...draft.chestWeapons, i].sort((a, b) => a - b) : draft.chestWeapons.filter((k) => k !== i);
        sound.click();
        refresh();
      };
    });
    const close = () => overlay.remove();
    overlay.querySelector<HTMLElement>('[data-a="gdd"]')!.onclick = () => { draft = defaultArsenal(); sound.click(); refresh(); };
    overlay.querySelector<HTMLElement>('[data-a="cancel"]')!.onclick = () => { sound.click(); close(); };
    overlay.querySelector<HTMLElement>('[data-a="ok"]')!.onclick = () => {
      current = draft;
      save(current);
      show();
      sound.click();
      close();
      onChange?.();
    };
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });
    refresh();
  };

  return () => ({ ammo: [...current.ammo], chestWeapons: [...current.chestWeapons] });
}
